/**
 * US-standard National Provider Identifier (NPI) utilities.
 *
 * An NPI is a unique 10-digit identification number issued to healthcare
 * providers in the United States by the Centers for Medicare & Medicaid Services (CMS).
 *
 * Luhn Checksum Algorithm with 80840 prefix:
 * The 10th digit is a check digit calculated using the Luhn formula (modulus 10).
 * For US healthcare applications, the 5-digit prefix "80840" (80 = health applications,
 * 840 = United States) is prepended to create a 15-digit number before computing the Luhn check:
 *
 * Positions from the right (1-indexed, position 1 is the check digit at NPI index 9):
 * - Even positions from right (pos 2, 4, 6, 8, 10 -> NPI indices 8, 6, 4, 2, 0): multiplied by 2 (subtract 9 if > 9)
 * - Odd positions from right (pos 1, 3, 5, 7, 9 -> NPI indices 9, 7, 5, 3, 1): multiplied by 1
 * - Prefix digits '80840' at positions 15..11 from right contribute a constant sum of 24:
 *     pos 15 ('8'): 8 * 1 = 8
 *     pos 14 ('0'): 0 * 2 = 0
 *     pos 13 ('8'): 8 * 1 = 8
 *     pos 12 ('4'): 4 * 2 = 8
 *     pos 11 ('0'): 0 * 1 = 0
 *     Sum = 8 + 0 + 8 + 8 + 0 = 24.
 */

/**
 * Validates whether a given string is a valid 10-digit US NPI with correct Luhn checksum.
 */
export const isValidNpi = (npi?: string | null): boolean => {
  if (!npi || typeof npi !== "string") return false;
  const trimmed = npi.trim();
  if (!/^\d{10}$/.test(trimmed)) return false;

  let sum = 24; // Constant sum contribution for '80840' prefix
  for (let i = 0; i < 10; i++) {
    let digit = parseInt(trimmed[i], 10);
    // Even indices 0, 2, 4, 6, 8 correspond to positions 10, 8, 6, 4, 2 from the right
    if (i % 2 === 0) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  return sum % 10 === 0;
};

/**
 * Validates an NPI input value according to US standards.
 *
 * @param val The input value to validate
 * @param isRequired Whether the field is mandatory
 * @param requiredMessage Custom message when mandatory and empty
 * @returns Error message string or empty string if valid
 */
export const validateNpi = (
  val?: string | null,
  isRequired = false,
  requiredMessage = "Billing Provider NPI is required."
): string => {
  if (!val || !val.trim()) {
    return isRequired ? requiredMessage : "";
  }
  const trimmed = val.trim();
  if (!/^\d{10}$/.test(trimmed) || !isValidNpi(trimmed)) {
    return "Please enter a valid 10-digit NPI.";
  }
  return "";
};
